// ============================================================
// EcartCarte — « Est-ce que le programme apprend ? »
// ------------------------------------------------------------
// L'écart proposé → retenu, PAR BLOC, sur les corrections de gamme
// (28 septembre 2026). Il affichait 10 310 % : la médiane d'une quantité
// corrigée (1 → 207) et d'un identifiant de matière (10 → 12). Aucun
// temps n'y entrait.
//
// Deux médianes par bloc, et elles ne disent pas la même chose :
//  - SIGNÉE : le sens du biais. +150 % en mise en route veut dire que
//    Mertens retient 2,5 fois ce que l'agent propose ;
//  - ABSOLUE : la taille de l'erreur. C'est elle qui doit descendre.
//
// Sous le seuil (5 corrections dans le mois et le bloc), le serveur ne
// rend pas de médiane, et l'écran n'en dessine pas : il affiche
// l'effectif. Une courbe sur deux points se lit comme une tendance.
// ============================================================

import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Badge from '../../ui/Badge.jsx';
import { formatPct } from '../../lib/format.js';
import Sparkline from './Sparkline.jsx';

const BLOCS = [
  { cle: 'mise_en_route', label: 'Mise en route' },
  { cle: 'production', label: 'Production' },
];

const signe = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${formatPct(v)}`);

/** Le badge : il ne conclut que sur des mois AU-DESSUS du seuil. */
function verdict(ecart) {
  const tendances = BLOCS.map(({ cle }) => {
    const m = ecart.filter((r) => r.bloc === cle && !r.sous_seuil);
    return m.length < 2 ? null : m.at(-1).mediane_absolue - m[0].mediane_absolue;
  }).filter((t) => t != null);
  if (!tendances.length) return { tone: 'neutral', label: 'pas encore mesurable' };
  if (tendances.every((t) => t < 0)) return { tone: 'ok', label: 'l’écart descend' };
  return { tone: 'warn', label: 'l’écart ne descend pas' };
}

function Bloc({ bloc, lignes }) {
  const mesurables = lignes.filter((r) => !r.sous_seuil);
  const total = lignes.reduce((n, r) => n + r.corrections, 0);
  return (
    <div>
      <p className="caps">{bloc.label} — écart absolu médian</p>
      {mesurables.length ? (
        <Sparkline
          points={lignes.map((r) => ({ x: r.mois, y: r.mediane_absolue }))}
          sens="descend"
        />
      ) : (
        <p className="field__hint">
          {total} pièce(s) corrigée(s)
          {lignes.length ? ` (${lignes.map((r) => `${r.mois} : ${r.corrections}`).join(', ')})` : ''}.
          Il en faut {lignes[0]?.seuil ?? 5} dans un mois pour une médiane : pas de courbe.
        </p>
      )}
    </div>
  );
}

export default function EcartCarte({ ecart = [], acceptation = [] }) {
  const v = verdict(ecart);
  return (
    <Card>
      <CardHead title="Est-ce que le programme apprend ?">
        <Badge tone={v.tone}>{v.label}</Badge>
      </CardHead>
      <CardBody className="stack">
        <p className="field__hint">
          L’écart mesure de combien le deviseur corrige la <strong>gamme</strong> que l’agent
          propose : son premier jet contre les temps retenus, une fois par pièce, par bloc. Il doit{' '}
          <strong>descendre</strong>. Les lectures corrigées (une quantité, une matière) n’y
          entrent pas : une lecture est juste ou fausse, elle n’a pas d’écart en pourcentage.
        </p>

        <div className="grid-2">
          {BLOCS.map((b) => (
            <Bloc key={b.cle} bloc={b} lignes={ecart.filter((r) => r.bloc === b.cle)} />
          ))}
        </div>

        <Table
          caption="Mois par mois"
          columns={[
            { key: 'mois', label: 'Mois' },
            {
              key: 'bloc',
              label: 'Bloc',
              render: (r) => BLOCS.find((b) => b.cle === r.bloc)?.label ?? r.bloc,
            },
            // Le dénominateur d'abord : c'est lui qui dit si la suite a un sens.
            { key: 'corrections', label: 'Pièces corrigées' },
            {
              key: 'mediane_signee',
              label: 'Médiane signée',
              render: (r) => (r.sous_seuil ? `sous le seuil (${r.seuil})` : signe(r.mediane_signee)),
            },
            {
              key: 'mediane_absolue',
              label: 'Médiane absolue',
              render: (r) => (r.sous_seuil ? '—' : formatPct(r.mediane_absolue)),
            },
          ]}
          rows={ecart}
          getRowKey={(r) => `${r.mois}-${r.bloc}`}
        />

        <div>
          <p className="caps">Propositions gardées sans modification</p>
          <Sparkline points={acceptation.map((p) => ({ x: p.mois, y: p.taux }))} sens="monte" />
        </div>
      </CardBody>
    </Card>
  );
}
